import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { LuHistory } from "react-icons/lu";
import { useDispatch, useSelector } from "react-redux";
import { selectAccessToken } from "../selectors";
import { setUserData } from "../store/AuthActions";
import { fetchUser } from "../Utils/UserUtils";
import * as agentApi from "./api";
import ChatHistoryList from "./ChatHistoryList";
import ConfirmDialog from "./ConfirmDialog";
import ConversationView from "./ConversationView";
import "./agent.css";

const HANG_TIMEOUT_MS = 120000;
const BASE_PATH = "/agent";

function localId(prefix) {
    return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function kindOf(output) {
    if (output.type === "video" || /\.(mp4|mov|webm|mkv)(\?|$)/i.test(output.url)) return "video";
    return "image";
}

function mediaFromOutputs(messageId, outputs = []) {
    return outputs
        .filter((o) => o?.url)
        .map((o, index) => ({
            id: `${messageId}-out-${index}`,
            kind: kindOf(o),
            url: o.url,
            name: `result-${index + 1}.${kindOf(o) === "video" ? "mp4" : "png"}`,
        }));
}

function mediaFromAssets(assets) {
    const byMessage = new Map();
    for (const asset of assets) {
        const list = byMessage.get(asset.message_id) ?? [];
        list.push({
            id: `${asset.message_id}-${asset.index}`,
            index: asset.index,
            kind: /video/i.test(asset.kind || "") || /\.(mp4|mov|webm|mkv)$/i.test(asset.filename || "") ? "video" : "image",
            url: asset.download_url,
            name: asset.filename || `attachment-${asset.index}`,
        });
        byMessage.set(asset.message_id, list);
    }
    for (const list of byMessage.values()) list.sort((a, b) => a.index - b.index);
    return byMessage;
}

function messagesFromHistory(session, assets) {
    const raw = Array.isArray(session.messages) ? session.messages : session.messages?.results ?? [];
    const media = mediaFromAssets(assets);
    return raw
        .filter((m) => m.role === "user" || m.role === "assistant")
        .map((m, index) => ({
            id: m.id ?? `history-${index}`,
            role: m.role,
            content: m.status === "error" ? "" : m.content || "",
            media: media.get(m.id) ?? [],
            creditsUsed: m.credits_used ?? 0,
            error: m.status === "error" ? m.content || "Something went wrong. Please try again." : undefined,
        }));
}

export default function AgentChat() {
    const { sessionId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const accessToken = useSelector(selectAccessToken);

    const [sessions, setSessions] = useState([]);
    const [sessionsLoading, setSessionsLoading] = useState(true);
    const [messages, setMessages] = useState([]);
    const [isLoading, setIsLoading] = useState(Boolean(sessionId));
    const [isSending, setIsSending] = useState(false);
    const [statusText, setStatusText] = useState(null);
    const [error, setError] = useState(null);
    const [historyOpen, setHistoryOpen] = useState(false);
    const [pendingDelete, setPendingDelete] = useState(null); // the chat awaiting confirmation
    const [deleting, setDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState(null);

    const abortRef = useRef(null);
    const hangTimerRef = useRef(null);
    const optimisticIdRef = useRef(null);
    const blobUrlsRef = useRef([]);
    // The chat whose messages are already on screen because we just created it or were handed its
    // first message. Reloading it from the server would wipe the live stream.
    const liveSessionRef = useRef(null);
    const handedOffRef = useRef(null);
    // The home page creates the session and passes the first message along.
    const handedOff = location.state?.initialMessage;

    const refreshSessions = useCallback(async () => {
        try {
            const data = await agentApi.listSessions();
            setSessions(data?.results ?? []);
        } catch {
            // The history list is a convenience; the chat itself still works without it.
        } finally {
            setSessionsLoading(false);
        }
    }, []);

    const stopStream = useCallback(() => {
        clearTimeout(hangTimerRef.current);
        abortRef.current?.();
        abortRef.current = null;
    }, []);

    // A paid reply changes the balance shown in the navbar, so refresh the user.
    const refreshCredits = useCallback(async () => {
        const response = await fetchUser(accessToken);
        if (response?.status === "success") dispatch(setUserData(JSON.stringify(response.data)));
    }, [accessToken, dispatch]);

    useEffect(() => {
        refreshSessions();
    }, [refreshSessions]);

    useEffect(
        () => () => {
            stopStream();
            blobUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
        },
        [stopStream]
    );

    useEffect(() => {
        if (sessionId && (liveSessionRef.current === sessionId || handedOff)) {
            liveSessionRef.current = sessionId;
            setIsLoading(false);
            return undefined;
        }
        liveSessionRef.current = null;
        stopStream();
        setIsSending(false);
        setStatusText(null);
        setError(null);

        if (!sessionId) {
            setMessages([]);
            setIsLoading(false);
            return undefined;
        }

        let cancelled = false;
        let watch = null;
        const applySession = (session, assets) => {
            setMessages(messagesFromHistory(session, assets?.assets ?? []));
            if (session.active_turn) {
                setIsSending(true);
                setStatusText(session.active_turn.status || "Understanding your request…");
                return true;
            }
            setIsSending(false);
            setStatusText(null);
            return false;
        };
        const pull = () =>
            Promise.all([agentApi.getSession(sessionId), agentApi.listSessionAssets(sessionId)]).then(([session, assets]) => {
                if (cancelled) return false;
                const running = applySession(session, assets);
                if (!running && watch) {
                    clearInterval(watch);
                    watch = null;
                    refreshCredits();
                    refreshSessions();
                }
                return running;
            });
        setIsLoading(true);
        pull()
            .then((running) => {
                if (cancelled || !running) return;
                watch = setInterval(() => {
                    pull().catch(() => {});
                }, 2000);
            })
            .catch((err) => {
                if (cancelled) return;
                setMessages([]);
                setError(err.message);
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });
        return () => {
            cancelled = true;
            clearInterval(watch);
        };
    }, [sessionId, stopStream, handedOff, refreshCredits, refreshSessions]);

    const finishStream = useCallback(() => {
        clearTimeout(hangTimerRef.current);
        // A clarification leaves the stream open; close it so the next send starts clean.
        abortRef.current?.();
        abortRef.current = null;
        setIsSending(false);
        setStatusText(null);
        refreshSessions();
    }, [refreshSessions]);

    const armHangTimer = useCallback(() => {
        clearTimeout(hangTimerRef.current);
        hangTimerRef.current = setTimeout(() => {
            abortRef.current?.();
            setMessages((prev) => [
                ...prev,
                { id: localId("error"), role: "assistant", content: "", error: "Request timed out. Please try again." },
            ]);
            finishStream();
        }, HANG_TIMEOUT_MS);
    }, [finishStream]);

    const handleEvent = useCallback(
        (event) => {
            armHangTimer();
            switch (event.type) {
                case "status":
                    setStatusText(event.message);
                    break;
                case "user_message":
                    setMessages((prev) =>
                        prev.map((m) => (m.id === optimisticIdRef.current ? { ...m, id: event.message_id } : m))
                    );
                    optimisticIdRef.current = null;
                    break;
                case "assistant_message":
                    if (event.credits_used > 0) refreshCredits();
                    setMessages((prev) => [
                        ...prev.filter((m) => m.id !== event.message_id),
                        {
                            id: event.message_id,
                            role: "assistant",
                            content: event.content,
                            media: mediaFromOutputs(event.message_id, event.outputs),
                            creditsUsed: event.credits_used ?? 0,
                        },
                    ]);
                    break;
                case "clarification":
                    setMessages((prev) => [
                        ...prev,
                        {
                            id: localId("clarification"),
                            role: "assistant",
                            content: event.question,
                            options: event.options ?? [],
                        },
                    ]);
                    finishStream();
                    break;
                case "credits_held":
                    refreshCredits();
                    break;
                case "error":
                    refreshCredits();
                    setMessages((prev) => [
                        ...prev.filter((m) => m.id !== event.message_id),
                        {
                            id: event.message_id || localId("error"),
                            role: "assistant",
                            content: "",
                            error: event.message,
                            code: event.code,
                        },
                    ]);
                    finishStream();
                    break;
                case "done":
                    finishStream();
                    break;
                default:
                    break;
            }
        },
        [armHangTimer, finishStream, refreshCredits]
    );

    const handleSend = useCallback(
        async (content, files = []) => {
            const media = files.map((file, index) => {
                const url = URL.createObjectURL(file);
                blobUrlsRef.current.push(url);
                return {
                    id: `pending-${index}-${file.name}`,
                    kind: file.type.startsWith("video/") ? "video" : "image",
                    url,
                    name: file.name,
                };
            });
            const optimistic = { id: localId("optimistic"), role: "user", content, media };
            optimisticIdRef.current = optimistic.id;
            setMessages((prev) => [...prev, optimistic]);
            setIsSending(true);
            setStatusText(null);
            setError(null);

            try {
                let targetId = sessionId;
                if (!targetId) {
                    const session = await agentApi.createSession();
                    targetId = session.id;
                    liveSessionRef.current = targetId;
                    navigate(`${BASE_PATH}/${targetId}`, { replace: true });
                }
                abortRef.current = agentApi.streamMessage({
                    sessionId: targetId,
                    content: content || undefined,
                    images: files.length ? files : undefined,
                    onEvent: handleEvent,
                });
                armHangTimer();
            } catch (err) {
                setMessages((prev) => prev.filter((m) => m.id !== optimistic.id));
                setError(err.message || "Something went wrong. Please try again.");
                setIsSending(false);
            }
        },
        [sessionId, navigate, handleEvent, armHangTimer]
    );

    useEffect(() => {
        if (!handedOff || !sessionId || handedOffRef.current === sessionId) return undefined;
        // Deferred so React StrictMode's dev-only unmount/remount cancels the first attempt
        // instead of aborting a stream that already started.
        const timer = setTimeout(() => {
            handedOffRef.current = sessionId;
            // Clear the state so a refresh doesn't resend, then stream the first message.
            navigate(location.pathname, { replace: true, state: null });
            handleSend(handedOff.content, handedOff.files ?? []);
        }, 0);
        return () => clearTimeout(timer);
    }, [handedOff, sessionId, handleSend, navigate, location.pathname]);

    const confirmDelete = useCallback(async () => {
        if (!pendingDelete) return;
        setDeleting(true);
        setDeleteError(null);
        try {
            await agentApi.deleteSession(pendingDelete.id);
            setSessions((prev) => prev.filter((s) => s.id !== pendingDelete.id));
            if (pendingDelete.id === sessionId) navigate(BASE_PATH);
            setPendingDelete(null);
        } catch (err) {
            setDeleteError(err.message || "Couldn't delete this chat. Please try again.");
        } finally {
            setDeleting(false);
        }
    }, [pendingDelete, sessionId, navigate]);

    const cancelDelete = () => {
        setPendingDelete(null);
        setDeleteError(null);
    };

    const activeTitle = sessionId ? sessions.find((s) => s.id === sessionId)?.title || "Chat" : "New chat";

    const history = (
        <ChatHistoryList
            sessions={sessions}
            activeSessionId={sessionId ?? null}
            isLoading={sessionsLoading}
            onSelect={(id) => {
                setHistoryOpen(false);
                navigate(`${BASE_PATH}/${id}`);
            }}
            onCreate={() => {
                setHistoryOpen(false);
                navigate(BASE_PATH);
            }}
            onDelete={(id) => setPendingDelete(sessions.find((s) => s.id === id) ?? { id, title: "this chat" })}
        />
    );

    return (
        <div className="tryon-agent tryon-agent-surface relative flex h-[calc(100dvh-5rem)] min-h-[28rem] w-full gap-4 overflow-hidden p-4">
            <aside
                aria-label="Chat sessions"
                className="hidden h-full w-60 shrink-0 flex-col rounded-2xl bg-white p-3 shadow-[0_0_0_1px_var(--hairline)] md:flex"
            >
                {history}
            </aside>

            {historyOpen && (
                <>
                    <div className="fixed inset-0 z-40 bg-black/50 md:hidden" onClick={() => setHistoryOpen(false)} />
                    <aside className="fixed left-0 top-0 z-50 flex h-full w-64 flex-col bg-white p-3 shadow-[0_0_0_1px_var(--hairline)] md:hidden">
                        {history}
                    </aside>
                </>
            )}

            <main className="flex min-w-0 flex-1 flex-col overflow-hidden rounded-2xl bg-white shadow-[0_0_0_1px_var(--hairline)]">
                <header className="flex shrink-0 items-center gap-3 border-b border-[var(--border-primary)] px-4 py-3 sm:px-5">
                    <button
                        type="button"
                        onClick={() => setHistoryOpen(true)}
                        aria-label="Show chat history"
                        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-primary-200 bg-white text-primary-700 md:hidden"
                    >
                        <LuHistory size={15} />
                    </button>
                    <h1 className="min-w-0 truncate font-Mulish text-[15px] font-semibold text-secondary">{activeTitle}</h1>
                </header>
                <ConversationView
                    messages={messages}
                    isLoading={isLoading}
                    isSending={isSending}
                    statusText={statusText}
                    error={error}
                    onSend={handleSend}
                    onDismissError={() => setError(null)}
                />
            </main>
            <ConfirmDialog
                open={Boolean(pendingDelete)}
                title="Delete this chat?"
                message={`"${pendingDelete?.title ?? ""}" and every image in it will be deleted for good. This can't be undone.`}
                confirmLabel="Delete chat"
                busyLabel="Deleting…"
                busy={deleting}
                error={deleteError}
                onConfirm={confirmDelete}
                onCancel={cancelDelete}
            />
        </div>
    );
}
